import { first, isEmpty } from 'lodash';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from 'components';
import { P2P_ORDER_DEEPLINK_ID } from 'constants/deeplink.constants';
import { VIDEOS_PATH } from 'constants/images.constants';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PaymentDetailsViewProps } from 'types';
import { deeplinkUtils, userDeviceUtils } from 'utils';

import { SqDeeplinks } from '../SqDeeplinks';
import { SqInstructionSteps } from '../SqInstructionSteps';
import { SqPaymentDetailsRules } from '../SqPaymentDetailsRules';
import { SqPaymentFields } from '../SqPaymentFields';

const QuestionIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 2.5C6.75329 2.5 2.5 6.75329 2.5 12C2.5 17.2467 6.75329 21.5 12 21.5C17.2467 21.5 21.5 17.2467 21.5 12C21.5 6.75329 17.2467 2.5 12 2.5ZM1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12ZM12 9C11.1716 9 10.5 9.67157 10.5 10.5C10.5 10.9142 10.1642 11.25 9.75 11.25C9.33579 11.25 9 10.9142 9 10.5C9 8.84315 10.3431 7.5 12 7.5C13.6569 7.5 15 8.84315 15 10.5C15 11.7804 14.2066 12.874 13.0806 13.3136C13.027 13.3347 12.9925 13.3617 12.9754 13.3802C12.9681 13.3882 12.9649 13.3947 12.9638 13.3975C12.9634 13.3985 12.9631 13.3993 12.963 13.4C12.9613 13.6643 12.7485 13.875 12.4879 13.875C12.2239 13.875 12.0095 13.6606 12.0095 13.3966C12.0095 12.8065 12.4595 12.4571 12.8723 12.2989C13.5456 12.0407 14 11.322 14 10.5C14 9.67157 13.3284 9 12.5 9H12ZM12 14.5C12.4142 14.5 12.75 14.8358 12.75 15.25V15.2578C12.75 15.672 12.4142 16.0078 12 16.0078C11.5858 16.0078 11.25 15.672 11.25 15.2578V15.25C11.25 14.8358 11.5858 14.5 12 14.5Z"
      fill="#777777"
    />
  </svg>
);

export const SqPaymentDetails: React.FC<PaymentDetailsViewProps> = () => {
  const {
    order,
    deeplinks,
    desktopQrDeeplinks,
    currencySymbol,
    customerBankName,
    requisitesBankName,
    requisitesCountry,
    shouldShowQrCode,
    customerBankCode,
    paymentTypeFlags: { isMobileOperator, isDeeplinkSberbank },
  } = useOrderContext();

  const [instructionOpen, setInstructionOpen] = useState(!shouldShowQrCode);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    setVideoError(false);
  }, [customerBankCode]);

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const instructionCustomerBankName = useMemo(
    () => customerBankName || t('instruction_basic.fallback_customer_bank'),
    [customerBankName, t],
  );

  const instructionRequisitesBankName = useMemo(
    () => requisitesBankName || t('instruction_basic.fallback_bank'),
    [requisitesBankName, t],
  );

  const instructionCountry = useMemo(
    () => requisitesCountry || t('instruction_basic.fallback_country'),
    [requisitesCountry, t],
  );

  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);

  // on desktop the strict list may be empty while OS-pinned links can still be
  // offered through the QR handoff page
  const sqDeeplinks = useMemo(
    () => (isEmpty(deeplinks) ? desktopQrDeeplinks : deeplinks),
    [deeplinks, desktopQrDeeplinks],
  );

  const showsDeeplinkQrHint = useMemo(
    () =>
      !isEmpty(sqDeeplinks) &&
      deeplinkUtils.getSqDeeplinkDisplayMode(deviceOS).showQr,
    [sqDeeplinks, deviceOS],
  );

  const isDeeplinkHandoffQr = useMemo(
    () => first(sqDeeplinks)?.id !== P2P_ORDER_DEEPLINK_ID,
    [sqDeeplinks],
  );

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-pb-6 tw-gap-[10px]">
      <SqPaymentFields />

      {!isEmpty(sqDeeplinks) && <SqDeeplinks deeplinks={sqDeeplinks} />}

      {showsDeeplinkQrHint && !shouldShowQrCode && (
        <SqPaymentDetailsRules
          isQrMode
          isDeeplinkHandoffQr={isDeeplinkHandoffQr}
        />
      )}

      {shouldShowQrCode && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <SqPaymentDetailsRules isQrMode />
          <div className="tw-text-base tw-font-semibold tw-text-primary">
            {t('instruction_subtitle')}
          </div>
          {customerBankCode && !videoError ? (
            <video
              key={customerBankCode}
              controls
              playsInline
              preload="metadata"
              className="tw-w-full tw-rounded-xl tw-h-32"
              onError={(e) => {
                const video = e.currentTarget as any;
                // eslint-disable-next-line no-console
                console.log(
                  'MediaError code: ' +
                    video.error?.code +
                    ' - ' +
                    video.error?.message,
                );
                setVideoError(true);
              }}
            >
              <source
                src={`${VIDEOS_PATH}instruction/${customerBankCode}.mp4`}
                type="video/mp4"
              />
            </video>
          ) : (
            <SqInstructionSteps
              customerBank={instructionCustomerBankName}
              amount={order.amount}
              currencySymbol={currencySymbol}
              isQrMode
              isMobileOperator={isMobileOperator}
            />
          )}
        </div>
      )}

      {!shouldShowQrCode && (
        <Fragment>
          {!showsDeeplinkQrHint && (
            <div>
              <SqPaymentDetailsRules />
            </div>
          )}
          <div className="tw-text-center tw-mt-2">
            <div
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-secondary tw-cursor-pointer"
              onClick={() => setInstructionOpen(true)}
            >
              <QuestionIcon />
              {t('instruction_basic.trigger')}
            </div>
          </div>
        </Fragment>
      )}

      <Modal
        open={instructionOpen}
        mobileViewOnly
        onClose={() => setInstructionOpen(false)}
        title={t('instruction_basic.title')}
        footer={
          <Button
            type="button"
            data-testid="sq-instruction-ok-button"
            heightClassName="tw-h-[54px]"
            className="tw-rounded-full"
            onClick={() => setInstructionOpen(false)}
          >
            {t('instruction_basic.ok')}
          </Button>
        }
      >
        <SqInstructionSteps
          bank={instructionRequisitesBankName}
          customerBank={instructionCustomerBankName}
          country={instructionCountry}
          amount={order.amount}
          currencySymbol={currencySymbol}
          isDeeplinkSberbank={isDeeplinkSberbank}
          isMobileOperator={isMobileOperator}
        />
      </Modal>
    </div>
  );
};
